/**
 * Operational Care Milestone Templates — TCPN PHASE 10-B2B M1.B
 * =============================================================
 * Provider-facing CRUD for inert operational milestone templates.
 * Fully decoupled from escrow/payment milestones.
 *
 * Backend (JWT-bound, gated by `custom_milestones`):
 *   GET    /api/clinic/milestone-templates[?include_archived=true]
 *   POST   /api/clinic/milestone-templates
 *   PATCH  /api/clinic/milestone-templates/{template_id}
 *   DELETE /api/clinic/milestone-templates/{template_id}   (soft archive)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Switch } from '../../../components/ui/switch';
import { Badge } from '../../../components/ui/badge';
import { Toaster, toast } from 'sonner';
import {
  Plus, Edit, Archive, Loader2, Lock, ArrowLeft,
  ListChecks, RotateCcw, X
} from 'lucide-react';
import api from '../../../utils/api';

const ENDPOINT = '/clinic/milestone-templates';

const emptyForm = () => ({
  title: '',
  description: '',
  recommended_day_offset: 0,
  is_optional: false,
  display_order: 0,
  is_active: true,
});

const MilestoneTemplatesPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [includeArchived, setIncludeArchived] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const params = includeArchived ? { include_archived: true } : {};
      const res = await api.get(ENDPOINT, { params });
      if (res?.data?.success) {
        setTemplates(res.data.templates || []);
        setLocked(false);
      } else {
        setTemplates([]);
      }
    } catch (err) {
      const status = err?.response?.status;
      const code = err?.response?.data?.error;
      if (status === 403 && code === 'FEATURE_NOT_ENTITLED') {
        setLocked(true);
        setTemplates([]);
      } else if (status === 401 || status === 403) {
        toast.error(t('provider.milestones.toasts.accessDenied'));
        setTemplates([]);
      } else {
        toast.error(err?.response?.data?.message || t('provider.milestones.toasts.loadFailed'));
      }
    } finally {
      setLoading(false);
    }
  }, [includeArchived, t]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setModalOpen(true);
  };

  const openEdit = (tpl) => {
    setEditing(tpl.template_id);
    setForm({
      title: tpl.title || '',
      description: tpl.description || '',
      recommended_day_offset: tpl.recommended_day_offset ?? 0,
      is_optional: !!tpl.is_optional,
      display_order: tpl.display_order ?? 0,
      is_active: tpl.is_active !== false,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setEditing(null);
    setForm(emptyForm());
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error(t('provider.milestones.toasts.titleRequired'));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description?.trim() || null,
        recommended_day_offset: Number(form.recommended_day_offset) || 0,
        is_optional: !!form.is_optional,
        display_order: Number(form.display_order) || 0,
        is_active: !!form.is_active,
      };
      const res = editing
        ? await api.patch(`${ENDPOINT}/${editing}`, payload)
        : await api.post(ENDPOINT, payload);
      if (!res?.data?.success) {
        throw new Error(res?.data?.message || 'Save failed');
      }
      toast.success(editing ? t('provider.milestones.toasts.updated') : t('provider.milestones.toasts.created'));
      closeModal();
      fetchTemplates();
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || t('provider.milestones.toasts.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async (tpl) => {
    if (!window.confirm(t('provider.milestones.confirms.archive', { title: tpl.title }))) return;
    try {
      const res = await api.delete(`${ENDPOINT}/${tpl.template_id}`);
      if (!res?.data?.success) throw new Error(res?.data?.message || 'Archive failed');
      toast.success(t('provider.milestones.toasts.archived'));
      fetchTemplates();
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || t('provider.milestones.toasts.archiveFailed'));
    }
  };

  if (locked) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Toaster />
        <div className="max-w-3xl mx-auto px-6 py-12">
          <Button variant="ghost" onClick={() => navigate('/clinic/admin')} className="mb-6" data-testid="back-to-dashboard">
            <ArrowLeft className="h-4 w-4 mr-2" /> {t('provider.milestones.back')}
          </Button>
          <Card className="border-2 border-amber-200 bg-amber-50/40">
            <CardContent className="p-12 text-center" data-testid="milestone-templates-locked">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-100 mb-4">
                <Lock className="h-7 w-7 text-amber-700" />
              </div>
              <h2 className="text-2xl font-semibold text-slate-900 mb-2">
                {t('provider.milestones.locked.title')}
              </h2>
              <p className="text-slate-700 max-w-lg mx-auto mb-6">
                {t('provider.milestones.locked.body')}
              </p>
              <Button onClick={() => navigate('/clinic/upgrade')} data-testid="upgrade-cta">
                {t('provider.milestones.locked.cta')}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster />
      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/clinic/admin')} className="mb-2 -ml-3" data-testid="back-to-dashboard">
              <ArrowLeft className="h-4 w-4 mr-1" /> {t('provider.milestones.dashboardBack')}
            </Button>
            <h1 className="text-3xl font-semibold text-slate-900 flex items-center gap-2">
              <ListChecks className="h-7 w-7 text-blue-600" />
              {t('provider.milestones.title')}
            </h1>
            <p className="text-sm text-slate-600 mt-1 max-w-2xl">
              {t('provider.milestones.subtitle')}
            </p>
          </div>
          <Button onClick={openCreate} data-testid="add-milestone-template-btn">
            <Plus className="h-4 w-4 mr-2" /> {t('provider.milestones.add')}
          </Button>
        </div>

        <div className="flex items-center gap-3 mb-4">
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
              className="rounded border-slate-300"
              data-testid="toggle-archived"
            />
            {t('provider.milestones.showArchived')}
          </label>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-500">
            <Loader2 className="h-5 w-5 mr-2 animate-spin" /> {t('provider.milestones.loading')}
          </div>
        ) : templates.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center text-slate-600" data-testid="milestone-templates-empty">
              <ListChecks className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-base font-medium text-slate-800">{t('provider.milestones.empty.title')}</p>
              <p className="text-sm text-slate-500 mt-1 mb-4">
                {t('provider.milestones.empty.subtitle')}
              </p>
              <Button onClick={openCreate} variant="outline" data-testid="add-first-template-btn">
                <Plus className="h-4 w-4 mr-2" /> {t('provider.milestones.empty.cta')}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2" data-testid="milestone-templates-list">
            {templates.map((tpl) => (
              <Card key={tpl.template_id} className={tpl.archived_at ? 'opacity-60' : ''}>
                <CardContent className="py-4 px-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-mono text-slate-400">#{tpl.display_order}</span>
                        <h3 className="text-base font-medium text-slate-900 truncate">{tpl.title}</h3>
                        {tpl.is_optional && (
                          <Badge variant="outline" className="text-xs">{t('provider.milestones.badges.optional')}</Badge>
                        )}
                        {tpl.archived_at && (
                          <Badge variant="secondary" className="text-xs">{t('provider.milestones.badges.archived')}</Badge>
                        )}
                        {!tpl.is_active && !tpl.archived_at && (
                          <Badge variant="outline" className="text-xs text-amber-700 border-amber-300">{t('provider.milestones.badges.inactive')}</Badge>
                        )}
                      </div>
                      {tpl.description && (
                        <p className="text-sm text-slate-600 mt-1 line-clamp-2">{tpl.description}</p>
                      )}
                      <div className="flex items-center gap-3 text-xs text-slate-500 mt-2">
                        <span>{t('provider.milestones.dayOffset')} <strong>{tpl.recommended_day_offset}</strong></span>
                        <span className="text-slate-300">·</span>
                        <span className="font-mono">{tpl.template_id}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {!tpl.archived_at && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEdit(tpl)}
                            data-testid={`edit-template-${tpl.template_id}`}
                            aria-label={t('provider.milestones.aria.edit')}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleArchive(tpl)}
                            data-testid={`archive-template-${tpl.template_id}`}
                            aria-label={t('provider.milestones.aria.archive')}
                          >
                            <Archive className="h-4 w-4 text-slate-500" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="milestone-template-modal">
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-xl font-semibold text-slate-900">
                  {editing ? t('provider.milestones.modal.editTitle') : t('provider.milestones.modal.newTitle')}
                </h2>
                <Button variant="ghost" size="sm" onClick={closeModal} disabled={saving} data-testid="close-modal-btn">
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="title">{t('provider.milestones.modal.titleLabel')}</Label>
                  <Input
                    id="title"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    maxLength={120}
                    placeholder={t('provider.milestones.modal.titlePlaceholder')}
                    data-testid="input-title"
                  />
                </div>

                <div>
                  <Label htmlFor="description">{t('provider.milestones.modal.descriptionLabel')}</Label>
                  <Textarea
                    id="description"
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    maxLength={1500}
                    rows={3}
                    placeholder={t('provider.milestones.modal.descriptionPlaceholder')}
                    data-testid="input-description"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="day-offset">{t('provider.milestones.modal.dayOffsetLabel')}</Label>
                    <Input
                      id="day-offset"
                      type="number"
                      value={form.recommended_day_offset}
                      onChange={(e) => setForm({ ...form, recommended_day_offset: e.target.value })}
                      min={-365}
                      max={365}
                      data-testid="input-day-offset"
                    />
                    <p className="text-xs text-slate-500 mt-1">{t('provider.milestones.modal.dayOffsetHint')}</p>
                  </div>
                  <div>
                    <Label htmlFor="display-order">{t('provider.milestones.modal.displayOrderLabel')}</Label>
                    <Input
                      id="display-order"
                      type="number"
                      value={form.display_order}
                      onChange={(e) => setForm({ ...form, display_order: e.target.value })}
                      min={0}
                      max={9999}
                      data-testid="input-display-order"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2.5">
                  <div>
                    <p className="text-sm font-medium text-slate-800">{t('provider.milestones.modal.optionalTitle')}</p>
                    <p className="text-xs text-slate-500">{t('provider.milestones.modal.optionalHint')}</p>
                  </div>
                  <Switch
                    checked={!!form.is_optional}
                    onCheckedChange={(v) => setForm({ ...form, is_optional: v })}
                    data-testid="switch-optional"
                  />
                </div>

                <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2.5">
                  <div>
                    <p className="text-sm font-medium text-slate-800">{t('provider.milestones.modal.activeTitle')}</p>
                    <p className="text-xs text-slate-500">{t('provider.milestones.modal.activeHint')}</p>
                  </div>
                  <Switch
                    checked={!!form.is_active}
                    onCheckedChange={(v) => setForm({ ...form, is_active: v })}
                    data-testid="switch-active"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <Button variant="outline" onClick={closeModal} disabled={saving} data-testid="cancel-btn">
                  {t('provider.milestones.modal.cancel')}
                </Button>
                <Button onClick={handleSave} disabled={saving} data-testid="save-template-btn">
                  {saving ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t('provider.milestones.modal.saving')}</>
                  ) : editing ? (
                    <><RotateCcw className="h-4 w-4 mr-2" /> {t('provider.milestones.modal.update')}</>
                  ) : (
                    <><Plus className="h-4 w-4 mr-2" /> {t('provider.milestones.modal.create')}</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default MilestoneTemplatesPage;
